import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { FormGroup, Alert, Spinner } from "../components/UI";
import dayjs from "dayjs";

export default function SitzungForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [gremien, setGremien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sitzungsorte, setSitzungsorte] = useState([]);
  const [ortModus, setOrtModus] = useState("vordefiniert");
  const [showOrtModal, setShowOrtModal] = useState(false);
  const [ortFelder, setOrtFelder] = useState({ liegenschaft: "", strasse: "", hausnummer: "", plz: "", ort: "", raum: "" });
  const [antragsfristTage, setAntragsfristTage] = useState(7);

  useEffect(() => {
    api.getSitzungsorte().then(setSitzungsorte).catch(() => {});
  }, []);

  useEffect(() => {
    api.getEinstellungenFlat().then(e => {
      setAntragsfristTage(parseInt(e.standard_antragsfrist_tage || "7"));
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    titel: "", gremium_id: "", datum: "", ort: "", notizen: "", antraege_frist: "", antraege_frist: ""
  });

  useEffect(() => {
    api.getSitzungsorte().then(setSitzungsorte).catch(() => {});
  }, []);

  useEffect(() => {
    // Standard-Antragsfrist aus Einstellungen laden
    if (!id) {
      api.getEinstellungenFlat().then(e => {
        const tage = parseInt(e.standard_antragsfrist_tage || "7");
        // Wird vom Backend automatisch gesetzt
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    api.getSitzungsorte().then(setSitzungsorte).catch(() => {});
  }, []);

  useEffect(() => {
    const promises = [api.getGremien("sitzung.erstellen")];
    if (isEdit) promises.push(api.getSitzung(id));
    Promise.all(promises).then(([g, s]) => {
      setGremien(g);
      if (s) setForm({
        titel: s.titel,
        gremium_id: s.gremium_id,
        datum: dayjs(s.datum).format("YYYY-MM-DDTHH:mm"),
        ort: s.ort || "",
        notizen: s.notizen || "",
        antraege_frist: s.antraege_frist ? dayjs(s.antraege_frist).format("YYYY-MM-DDTHH:mm") : "",
        antraege_frist: s.antraege_frist ? dayjs(s.antraege_frist).format("YYYY-MM-DDTHH:mm") : "",
      });
      if (g.length > 0 && !s) setForm(f => ({ ...f, gremium_id: g[0].id }));
    }).finally(() => setLoading(false));
  }, [id]);

  const save = async () => {
    if (!form.titel || !form.gremium_id) {
      setError("Bitte Titel, Gremium und Datum ausfüllen."); return;
    }
    try {
      const data = { ...form, gremium_id: Number(form.gremium_id) };
      // Empty strings → null für Optional[datetime]-Felder (Pydantic akzeptiert nur null/datetime)
      if (!data.datum) data.datum = null;
      if (!data.antraege_frist) data.antraege_frist = null;
      if (!data.ende_zeit) data.ende_zeit = null;
      if (isEdit) await api.updateSitzung(id, data);
      else { const s = await api.createSitzung(data); navigate(`/sitzungen/${s.id}`); return; }
      navigate(`/sitzungen/${id}`);
    } catch { setError("Fehler beim Speichern."); }
  };

  if (loading) return <Spinner />;

  const ortModalSave = () => {
    const { liegenschaft, strasse, hausnummer, plz, ort, raum } = ortFelder;
    const parts = [liegenschaft, [strasse, hausnummer].filter(Boolean).join(" "), [plz, ort].filter(Boolean).join(" "), raum ? "Raum: " + raum : ""].filter(Boolean);
    setForm(f => ({ ...f, ort: parts.join(", ") }));
    setShowOrtModal(false);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="breadcrumb" style={{ cursor: "pointer" }} onClick={() => navigate("/sitzungen")}>
            Sitzungen /
          </div>
          <h2>{isEdit ? "Sitzung bearbeiten" : "Neue Sitzung"}</h2>
        </div>
      </div>

      <div className="content">
        {error && <Alert type="error">{error}</Alert>}

        <div className="card" style={{ maxWidth: 640 }}>
          <div className="card-body">
            <FormGroup label="Titel *">
              <input className="form-control" value={form.titel}
                placeholder="z. B. Jahreshauptversammlung 2025"
                onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} />
            </FormGroup>

            <FormGroup label="Gremium *">
              {gremien.length === 0 ? (
                <div style={{ padding: "8px 0", color: "#999", fontSize: 13 }}>
                  Kein Gremium vorhanden — bitte zuerst unter <strong>Gremien</strong> anlegen.
                </div>
              ) : (
                <select className="form-control" value={form.gremium_id}
                  onChange={e => setForm(f => ({ ...f, gremium_id: e.target.value }))}>
                  <option value="">— Gremium wählen —</option>
                  {gremien.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              )}
            </FormGroup>

            <div className="form-row">
              <FormGroup label="Datum & Uhrzeit (optional bei Platzhalter-Sitzungen)">
                <input type="datetime-local" className="form-control" value={form.datum}
                  onChange={e => {
          const datum = e.target.value;
          const frist = datum ? dayjs(datum).subtract(antragsfristTage, "day").format("YYYY-MM-DDTHH:mm") : "";
          setForm(f => ({ ...f, datum, antraege_frist: f.antraege_frist || frist }));
        }} />
              </FormGroup>
              <FormGroup label="Sitzungsort">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button type="button"
                    className={ortModus === "vordefiniert" ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
                    onClick={() => setOrtModus("vordefiniert")}>📍 Vordefiniert</button>
                  <button type="button"
                    className={ortModus === "eigen" ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
                    onClick={() => setOrtModus("eigen")}>✎ Eigene Eingabe</button>
                </div>
                {ortModus === "vordefiniert" ? (
                  <select className="form-control" value={form.ort || ""}
                    onChange={e => setForm(f => ({ ...f, ort: e.target.value }))}>
                    <option value="">— Bitte wählen —</option>
                    {sitzungsorte.map(o => (
                      <option key={o.id} value={`${o.name}, ${o.strasse || ""} ${o.hausnummer || ""}, ${o.plz || ""} ${o.ort || ""}${o.raum ? ", Raum: " + o.raum : ""}`.trim()}>
                        {o.name}{o.raum ? ` (${o.raum})` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowOrtModal(true)}>
                      📍 {form.ort || "Ort eingeben..."}
                    </button>
                    {form.ort && <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>{form.ort}</div>}
                  </div>
                )}
              </FormGroup>
            </div>

            <FormGroup label="Antragsfrist (TOPs einreichen bis)">
              <input type="datetime-local" className="form-control" value={form.antraege_frist}
                onChange={e => setForm(f => ({ ...f, antraege_frist: e.target.value }))} />
              <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>Nach Ablauf der Frist können nur noch Dringlichkeitsanträge gestellt werden</div>
            </FormGroup>

            <FormGroup label="Notizen">
              <textarea className="form-control" rows={3} value={form.notizen}
                placeholder="Interne Notizen zur Sitzung…"
                onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))} />
            </FormGroup>

            <hr className="divider" />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/sitzungen/${id}` : "/sitzungen")}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={save}>
                {isEdit ? "Änderungen speichern" : "Sitzung anlegen →"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showOrtModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 8, padding: 24, width: 440 }}>
            <h3 style={{ marginBottom: 16 }}>Sitzungsort eingeben</h3>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Liegenschaft / Name</div>
              <input className="form-control" value={ortFelder.liegenschaft}
                onChange={e => setOrtFelder(f => ({ ...f, liegenschaft: e.target.value }))}
                placeholder="z. B. Rathaus, Gerätehaus" />
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Straße</div>
                <input className="form-control" value={ortFelder.strasse}
                  onChange={e => setOrtFelder(f => ({ ...f, strasse: e.target.value }))} />
              </div>
              <div style={{ width: 80 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Nr.</div>
                <input className="form-control" value={ortFelder.hausnummer}
                  onChange={e => setOrtFelder(f => ({ ...f, hausnummer: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>PLZ</div>
                <input className="form-control" value={ortFelder.plz}
                  onChange={e => setOrtFelder(f => ({ ...f, plz: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Ort</div>
                <input className="form-control" value={ortFelder.ort}
                  onChange={e => setOrtFelder(f => ({ ...f, ort: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Raum (optional)</div>
              <input className="form-control" value={ortFelder.raum}
                onChange={e => setOrtFelder(f => ({ ...f, raum: e.target.value }))}
                placeholder="z. B. Schulungsraum EG" />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowOrtModal(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" onClick={ortModalSave}>Übernehmen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
